import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { EntityTypeEnum, TradingEntityClearingDetail, TradingEntityClearingType, WorkflowActions } from 'api'
import { AgGridWrapper, filteredColumnDefsBuilder } from 'view/components/datagrid'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { TradingEntityClearingDialog } from './TradingEntityClearingDialog'
import {
  directTradingEntityClearingPanelColumnDefs,
  sponsoredTradingEntityClearingPanelColumnDefs,
  tradingEntityClearingGridOptions,
  TradingEntityClearingPanelGridColumnId
} from './TradingEntityClearingPanelGridModel'
import {
  workflowTradingEntityClearingDetailsSelector,
  createWorkflowAvailableTradingEntityClearingClearingHouseIdsSelector
} from 'selectors/workflowSelectors'
import { canManageTradingEntityClearingSelector } from 'selectors/permissionSelectors'
import { CorrelatedResponseCallback, EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { Locale, Translation } from 'locale'
import { ClearingGridHeader, Wrapper } from './TradingEntityClearingPanelStyled'

type TradingEntityClearingPanelProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  tradingEntityId: string
  workflowId: string
  clearingType: TradingEntityClearingType
}

interface StateProps {
  getTradingEntityClearingDetails: (workflowId: string) => TradingEntityClearingDetail[]
  canManageTradingEntityClearings: boolean
  availableDirectClearingHouses: string[]
  availableSponsoredClearingHouses: string[]
}

interface DispatchProps {
  addNewTradingEntityClearing: (
    workflowId: string,
    entityTypeEnum: EntityTypeEnum,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: TradingEntityClearingPanelProps) => ownProps.workflowId

const mapStateToProps = createStructuredSelector({
  getTradingEntityClearingDetails: workflowTradingEntityClearingDetailsSelector,
  canManageTradingEntityClearings: canManageTradingEntityClearingSelector,
  availableDirectClearingHouses: createWorkflowAvailableTradingEntityClearingClearingHouseIdsSelector(
    workflowIdSelector,
    () => TradingEntityClearingType.DIRECT
  ),
  availableSponsoredClearingHouses: createWorkflowAvailableTradingEntityClearingClearingHouseIdsSelector(
    workflowIdSelector,
    () => TradingEntityClearingType.SPONSORED
  )
})

const mapDispatchToProps = {
  addNewTradingEntityClearing: WorkflowActions.createAddToExistingWorkflow
}

// TODO // James - Split this out into 2 separate files direct/sponsored
const TradingEntityClearingPanelComponent: React.FunctionComponent<TradingEntityClearingPanelProps> = props => {
  const {
    workflowId,
    getTradingEntityClearingDetails,
    canManageTradingEntityClearings,
    clearingType,
    addNewTradingEntityClearing,
    showModal,
    tradingEntityId,
    availableDirectClearingHouses,
    availableSponsoredClearingHouses
  } = props

  const headingText =
    clearingType === TradingEntityClearingType.DIRECT
      ? Translation.TC_DIRECT_TRADING_ENTITY_CLEARING
      : Translation.TC_SPONSORED_TRADING_ENTITY_CLEARING

  const buttonText =
    clearingType === TradingEntityClearingType.DIRECT
      ? Translation.TC_BUTTON_NEW_DIRECT_TRADING_ENTITY_CLEARING
      : Translation.TC_BUTTON_NEW_SPONSORED_TRADING_ENTITY_CLEARING

  const gridData = useMemo(() => {
    return getTradingEntityClearingDetails(workflowId).filter(
      (tradingEntityClearingDetail: TradingEntityClearingDetail) => tradingEntityClearingDetail.tradingEntityClearing.type === clearingType
    )
  }, [getTradingEntityClearingDetails, workflowId, clearingType])

  const availableClearingHouses =
    clearingType === TradingEntityClearingType.DIRECT ? availableDirectClearingHouses : availableSponsoredClearingHouses

  const noTradingEntityClearingsPlaceholder = useCallback(
    () => <div>{Locale.translate(Translation.SC_NO_TRADING_ENTITY_CLEARINGS)}</div>,
    []
  )

  const handleNewTradingEntityClearing = useCallback(() => {
    const correlatedResponseCallback = (localWorkflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
      return showModal(
        <TradingEntityClearingDialog
          tradingEntityClearingType={clearingType}
          tradingEntityId={tradingEntityId}
          workflowId={localWorkflowId}
          entityId={newlyAddedEntityIdAndTypes[EntityTypeEnum.TRADING_ENTITY_CLEARING]}
        />
      )
    }

    addNewTradingEntityClearing(workflowId, EntityTypeEnum.TRADING_ENTITY_CLEARING, correlatedResponseCallback)
  }, [addNewTradingEntityClearing, clearingType, showModal, tradingEntityId, workflowId])

  const filteredColumnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(
        clearingType === TradingEntityClearingType.DIRECT
          ? directTradingEntityClearingPanelColumnDefs
          : sponsoredTradingEntityClearingPanelColumnDefs
      )
        .without([TradingEntityClearingPanelGridColumnId.CLEARING_RELATIONSHIP_ACTIONS])
        .build(),
    [clearingType]
  )

  return (
    <Wrapper>
      <ClearingGridHeader>
        <label>{Locale.translate(headingText)}</label>
        {canManageTradingEntityClearings && (
          <TextButton
            dataTestId={'TradingEntityClearingPanel-TextButton-ButtonText'}
            type={TextButtonType.Primary}
            onClick={handleNewTradingEntityClearing}
            disabled={availableClearingHouses.length === 0}>
            {Locale.translate(buttonText)}
          </TextButton>
        )}
      </ClearingGridHeader>
      <AgGridWrapper
        className="datagrid-borderless"
        gridOptions={tradingEntityClearingGridOptions}
        columnDefs={filteredColumnDefs}
        rowData={gridData}
        placeholder={noTradingEntityClearingsPlaceholder}
        context={{
          workflowId,
          tradingEntityId
        }}
      />
    </Wrapper>
  )
}

export const TradingEntityClearingPanel = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(TradingEntityClearingPanelComponent)
)
